// Mi clave y los usuarios de la app.
//
// Ojo: la app no tiene permisos. Todo usuario que se cree acá ve y toca todo.
// La pantalla lo dice en voz alta para que no sea una sorpresa.

import { api } from "../api";
import { esc } from "../html";
import { iconoMas } from "../iconos";
import { formatoLocal } from "../../lib/fecha";
import type { Sesion } from "../../lib/tipos";

interface Usuario {
  id: number;
  usuario: string;
  activo: number;
  creado_en: string;
  sesiones: number;
}

function fila(u: Usuario, yo: string): string {
  const soyYo = u.usuario === yo;
  return `
    <div class="fila p-3 ${u.activo ? "" : "opacity-60"}">
      <div class="flex items-center gap-2">
        <span class="min-w-0 flex-1 truncate font-medium">${esc(u.usuario)}</span>
        ${soyYo ? `<span class="chip chip-acento">vos</span>` : ""}
        ${u.activo ? "" : `<span class="chip">de baja</span>`}
        ${
          u.activo && u.sesiones
            ? `<span class="chip chip-ok">${u.sesiones} ${u.sesiones === 1 ? "sesión" : "sesiones"}</span>`
            : ""
        }
      </div>
      <div class="mt-1 text-xs text-apagado">desde el ${formatoLocal(u.creado_en.slice(0, 10))}</div>
      ${
        u.activo
          ? `<div class="mt-2 flex flex-wrap gap-2">
               <button class="bClave btn-sec px-3 py-1 text-sm" data-id="${u.id}" data-usuario="${esc(u.usuario)}">
                 Ponerle otra clave
               </button>
               ${
                 soyYo
                   ? ""
                   : `<button class="bBaja btn-sec px-3 py-1 text-sm text-apagado" data-id="${u.id}">Dar de baja</button>`
               }
             </div>`
          : ""
      }
    </div>`;
}

export async function renderCuenta(contenedor: HTMLElement): Promise<void> {
  async function pintar(): Promise<void> {
    const [sesion, usuarios] = await Promise.all([
      api.get<Sesion>("/api/sesion"),
      api.get<Usuario[]>("/api/usuarios"),
    ]);

    contenedor.innerHTML = `
      <h1 class="titulo-pagina">Mi clave y usuarios</h1>
      <p class="subtitulo">Entraste como <b class="text-texto">${esc(sesion.usuario)}</b>.</p>

      <section class="mt-5">
        <h2 class="mb-2 text-sm font-semibold uppercase tracking-wider text-apagado">Cambiar mi clave</h2>
        <form id="fClave" class="tarjeta space-y-3">
          <label><span class="etiqueta">Clave actual</span>
            <input id="actual" type="password" class="campo" autocomplete="current-password" required /></label>
          <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <label><span class="etiqueta">Clave nueva</span>
              <input id="nueva" type="password" class="campo" autocomplete="new-password" minlength="8" required /></label>
            <label><span class="etiqueta">Repetila</span>
              <input id="repetir" type="password" class="campo" autocomplete="new-password" minlength="8" required /></label>
          </div>
          <p class="text-xs text-apagado">
            Al menos 8 caracteres. Una frase larga que te acuerdes es mejor que ocho símbolos raros.
            Cambiarla cierra tu sesión en los otros dispositivos.
          </p>
          <p id="errClave" class="text-sm text-alerta"></p>
          <p id="okClave" class="text-sm text-ok"></p>
          <button class="btn-primario w-full">Cambiar la clave</button>
        </form>
      </section>

      <section class="mt-6">
        <h2 class="mb-2 text-sm font-semibold uppercase tracking-wider text-apagado">
          Usuarios <span class="font-normal">(${usuarios.filter((u) => u.activo).length} activos)</span>
        </h2>

        <div class="tarjeta mb-2 border-l-2 border-alerta text-sm text-apagado">
          La app <b class="text-texto">no tiene permisos por módulo</b>: cualquier usuario que crees
          va a ver y poder tocar todo, incluidos precios, presupuestos y clientes.
        </div>

        <div class="space-y-2">${usuarios.map((u) => fila(u, sesion.usuario)).join("")}</div>

        <details class="tarjeta mt-3">
          <summary class="flex items-center gap-2 font-medium text-acento">${iconoMas} Usuario nuevo</summary>
          <form id="fNuevo" class="mt-4 space-y-3">
            <label><span class="etiqueta">Nombre de usuario</span>
              <input id="nombre" class="campo" required minlength="3" maxlength="60"
                     autocomplete="off" placeholder="sin espacios" /></label>
            <label><span class="etiqueta">Clave</span>
              <input id="clave" type="password" class="campo" required minlength="8"
                     autocomplete="new-password" /></label>
            <p id="errNuevo" class="text-sm text-alerta"></p>
            <button class="btn-primario w-full">Crear usuario</button>
          </form>
        </details>
      </section>`;

    const fClave = contenedor.querySelector("#fClave") as HTMLFormElement;
    fClave.addEventListener("submit", async (ev) => {
      ev.preventDefault();
      const err = contenedor.querySelector("#errClave") as HTMLElement;
      const ok = contenedor.querySelector("#okClave") as HTMLElement;
      err.textContent = "";
      ok.textContent = "";
      const nueva = (contenedor.querySelector("#nueva") as HTMLInputElement).value;
      const repetir = (contenedor.querySelector("#repetir") as HTMLInputElement).value;
      if (nueva !== repetir) {
        err.textContent = "Las dos claves nuevas no coinciden";
        return;
      }
      try {
        await api.put("/api/clave", {
          actual: (contenedor.querySelector("#actual") as HTMLInputElement).value,
          nueva,
        });
        fClave.reset();
        ok.textContent = "Listo, clave cambiada.";
      } catch (e) {
        err.textContent = e instanceof Error ? e.message : "No se pudo cambiar";
      }
    });

    const fNuevo = contenedor.querySelector("#fNuevo") as HTMLFormElement;
    fNuevo.addEventListener("submit", async (ev) => {
      ev.preventDefault();
      const err = contenedor.querySelector("#errNuevo") as HTMLElement;
      err.textContent = "";
      try {
        await api.post("/api/usuarios", {
          usuario: (contenedor.querySelector("#nombre") as HTMLInputElement).value.trim(),
          clave: (contenedor.querySelector("#clave") as HTMLInputElement).value,
        });
        await pintar();
      } catch (e) {
        err.textContent = e instanceof Error ? e.message : "No se pudo crear";
      }
    });

    contenedor.querySelectorAll<HTMLButtonElement>(".bClave").forEach((b) => {
      b.addEventListener("click", async () => {
        const clave = prompt(`Clave nueva para «${b.dataset.usuario}» (mínimo 8 caracteres):`);
        if (!clave) return;
        try {
          await api.put(`/api/usuarios/${b.dataset.id}`, { clave });
          alert("Clave cambiada. Se le cerraron todas las sesiones.");
          await pintar();
        } catch (e) {
          alert(e instanceof Error ? e.message : "No se pudo cambiar");
        }
      });
    });

    contenedor.querySelectorAll<HTMLButtonElement>(".bBaja").forEach((b) => {
      b.addEventListener("click", async () => {
        if (!confirm("¿Dar de baja al usuario? Deja de poder entrar, pero no se borra nada.")) return;
        try {
          await api.del(`/api/usuarios/${b.dataset.id}`);
          await pintar();
        } catch (e) {
          alert(e instanceof Error ? e.message : "No se pudo dar de baja");
        }
      });
    });
  }

  try {
    await pintar();
  } catch (e) {
    contenedor.innerHTML = `<div class="tarjeta text-alerta">No se pudo cargar: ${esc(
      e instanceof Error ? e.message : "error desconocido",
    )}</div>`;
  }
}
